import React, { useState, useEffect } from 'react'
import { X, Check, Plus } from 'lucide-react'
import { Modal, Button, Input, AlertMessage } from '../ui'
import { KanbanStatus, ColorOption, VehicleStatusOption, VehicleStatus } from '../../types'
import { cn } from '../../utils/cn'

interface StatusFormModalProps {
 isOpen: boolean
 onClose: () => void
 onSave: (status: KanbanStatus) => void
 status?: KanbanStatus | null
 colorOptions: ColorOption[]
 vehicleStatusOptions: VehicleStatusOption[]
}

export const StatusFormModal: React.FC<StatusFormModalProps> = ({
 isOpen,
 onClose,
 onSave,
 status,
 colorOptions,
 vehicleStatusOptions
}) => {
 const [formData, setFormData] = useState<Partial<KanbanStatus>>({
 label: '',
 value: '',
 color: '#3B82F6',
 description: '',
 vehicleStatus: null
 })
 const [validationError, setValidationError] = useState<string | null>(null)

 const isEditMode = !!status
 const isLocked = status?.isFirst || status?.isClosed || status?.isLost

 useEffect(() => {
 if (status) {
 setFormData({
 id: status.id,
 label: status.label,
 value: status.value,
 color: status.color,
 description: status.description,
 vehicleStatus: status.vehicleStatus,
 isFirst: status.isFirst,
 isClosed: status.isClosed,
 isLost: status.isLost,
 locked: status.locked
 })
 } else {
 setFormData({
 label: '',
 value: '',
 color: '#3B82F6',
 description: '',
 vehicleStatus: null
 })
 }
 setValidationError(null)
 }, [status, isOpen])

 const handleSave = () => {
 if (!formData.label || !formData.description) {
 setValidationError('Por favor, preencha todos os campos obrigatórios (Nome e Descrição)')
 return
 }

 const value = formData.value || formData.label.toLowerCase().replace(/\s+/g, '_')
 
 onSave({
 ...formData,
 value,
 color: formData.color || '#3B82F6',
 label: formData.label!,
 description: formData.description!
 } as KanbanStatus)
 
 handleClose()
 }

 const handleClose = () => {
 setFormData({
 label: '',
 value: '',
 color: '#3B82F6',
 description: '',
 vehicleStatus: null
 })
 setValidationError(null)
 onClose()
 }

 return (
 <Modal
 isOpen={isOpen}
 onClose={handleClose}
 title={isEditMode ? 'Editar Etapa' : 'Adicionar Nova Etapa'}
 size="md"
 >
 <div className="space-y-4">
 {validationError && (
 <AlertMessage variant="error" message={validationError} />
 )}
 
 {/* Nome */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-1">
 Nome da Etapa *
 </label>
 <Input
 value={formData.label || ''}
 onChange={(e) => setFormData({ ...formData, label: e.target.value })}
 placeholder="Ex: Em Negociação"
 disabled={isLocked}
 />
 {isLocked && (
 <p className="text-xs text-muted-foreground mt-1">
 Este nome não pode ser alterado pois é uma etapa especial do sistema
 </p>
 )}
 </div>

 {/* Descrição */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-1">
 Descrição *
 </label>
 <textarea
 value={formData.description || ''}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 placeholder="Ex: Leads em processo de negociação"
 rows={3}
 className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
 />
 </div>

 {/* Cor */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor
 </label>
 <div className="grid grid-cols-5 gap-2">
 {colorOptions.map((color) => (
 <button
 key={color.value}
 type="button"
 onClick={() => !isLocked && setFormData({ ...formData, color: color.value })}
 disabled={isLocked}
 style={{ backgroundColor: color.value }}
 className={cn(
 'h-10 rounded-md border-2 transition-all',
 formData.color === color.value
 ? 'border-gray-900 scale-110 ring-2 ring-offset-2 ring-gray-900 dark:ring-gray-100'
 : 'border-border hover:scale-105',
 isLocked && 'opacity-50 cursor-not-allowed'
 )}
 title={color.label}
 />
 ))}
 </div>
 {isLocked && (
 <p className="text-xs text-muted-foreground mt-1">
 A cor desta etapa não pode ser alterada
 </p>
 )}
 </div>

 {/* Status do Veículo */}
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Status do Veículo
 <span className="text-xs text-muted-foreground ml-2">
 (Automático ao mover lead)
 </span>
 </label>
 <select
 value={formData.vehicleStatus || ''}
 onChange={(e) => setFormData({ 
 ...formData, 
 vehicleStatus: e.target.value === '' ? null : e.target.value as VehicleStatus
 })}
 className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
 >
 {vehicleStatusOptions.map((option) => (
 <option key={option.value || 'null'} value={option.value || ''}>
 {option.label}
 </option>
 ))}
 </select>
 <p className="text-xs text-muted-foreground mt-1">
 Quando um lead entrar nesta etapa, o veículo vinculado terá seu status alterado automaticamente
 </p>
 </div>

 {/* Ações */}
 <div className="flex gap-2 justify-end pt-4">
 <Button variant="outline" onClick={handleClose}>
 <X className="h-4 w-4 mr-2" />
 Cancelar
 </Button>
 <Button onClick={handleSave}>
 {isEditMode ? (
 <>
 <Check className="h-4 w-4 mr-2" />
 Salvar
 </>
 ) : (
 <>
 <Plus className="h-4 w-4 mr-2" />
 Adicionar
 </>
 )}
 </Button>
 </div>
 </div>
 </Modal>
 )
}

